/**
 * Upload Match — multi-file upload with game metadata.
 *
 * Collects game code, match date, team config, pitch dimensions,
 * game timing, and up to four files: video, tracking data, event data,
 * and a supplemental file with description.  At least one of video or
 * tracking data is required.
 */
import { AlertCircle, CheckCircle, ChevronDown, ChevronRight, Clock, Cog, Film, FileSpreadsheet, FileText, Loader2, Upload, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { trackEvent } from '../lib/analytics'
import type { Session } from '../lib/api'
import { ApiError, api } from '../lib/api'
import { uploadInParts } from './upload/multipart'
import './admin.css'
import './upload-match.css'

type PresignResponse = {
  /** Present on a single-PUT plan; absent on a multipart one. */
  upload_url?: string
  r2_key: string
  expires_in?: number
  job_id?: string
  /** R2 refuses a single PUT over ~4.995 GiB, so a large video comes back
   *  as a part plan the client drives instead. */
  multipart?: boolean
  part_size?: number
  total_parts?: number
}

type UploadState = 'idle' | 'presigning' | 'uploading' | 'completing' | 'processing' | 'done' | 'error'

type PendingJob = {
  id: string; game_code: string; match_date: string | null
  status: string; video_filename: string | null
  video_size_mb: number | null; uploaded_at: string | null
  error: string | null
}

type JobStatus = {
  job_id: string; status: string; progress: number
  message: string; error?: string; terminal?: boolean
  calibration_url?: string
}

type ExistingGame = {
  id: number; game_code: string; match_date: string | null
  has_video: boolean; has_tracking: boolean
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  pending: <Clock size={14} />,
  uploading: <Loader2 size={14} className="spin" />,
  // Processing runs for hours: a 1-second spinner reads as "any moment
  // now" and then as "stuck". A slowly turning cog reads as machinery
  // working at its own pace. Uploading keeps the spinner — it is
  // browser-side, minutes, with a live progress bar beside it.
  pod_launching: <Cog size={14} className="spin-slow" />,
  processing: <Cog size={14} className="spin-slow" />,
  calibration_review: <AlertCircle size={14} />,
  completed: <CheckCircle size={14} />,
  failed: <AlertCircle size={14} />,
  cancelled: <X size={14} />,
}

// What the user reads. Priori staff see the real pipeline state; everyone
// else sees only the phases they can act on — uploading, processing,
// completed/failed (and cancelled, which they caused). Internal stage
// names (pod_launching, calibration_review, and any stage the backend
// grows later) mean nothing outside Priori and read as errors.
const STATUS_LABELS: Record<string, string> = {
  pending: 'pending',
  uploading: 'uploading',
  calibration_review: 'needs review',
  completed: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
}
// Collapse any pipeline status to the only states a customer sees.
// Jobs are created at presign time, so 'pending' means the browser
// upload has not completed yet — it reads as uploading.
export const customerStatus = (s: string): string => {
  if (s === 'pending' || s === 'uploading') return 'uploading'
  if (s === 'completed' || s === 'failed' || s === 'cancelled') return s
  return 'processing'
}
// What a customer reads when an upload ends. The server sends the same
// words (workbench.py UPLOAD_RECEIVED / PROCESSING_NOT_STARTED).
export const UPLOAD_RECEIVED = 'Upload successful. Your match will now be processed.'
export const PROCESSING_NOT_STARTED =
  'Your upload was received, but processing could not be started. Please contact Priori.'
export const UPLOAD_FAILED =
  'Upload failed. Please try again, or contact Priori if it keeps happening.'

export const statusLabel = (s: string, isStaff: boolean) =>
  isStaff
    ? (STATUS_LABELS[s] ?? s.replace(/_/g, ' '))
    : (STATUS_LABELS[customerStatus(s)] ?? 'processing')

const JERSEY_COLORS = [
  { value: '', label: 'Select color' },
  { value: '#000000', label: 'Black' },
  { value: '#0000ff', label: 'Blue' },
  { value: '#8b4513', label: 'Brown' },
  { value: '#808080', label: 'Gray' },
  { value: '#008000', label: 'Green' },
  { value: '#ffa500', label: 'Orange' },
  { value: '#ffc0cb', label: 'Pink' },
  { value: '#800080', label: 'Purple' },
  { value: '#ff0000', label: 'Red' },
  { value: '#ffffff', label: 'White' },
  { value: '#ffff00', label: 'Yellow' },
]

/* ── helpers ──────────────────────────────────────────────────────────── */

function formatBytes(b: number): string {
  if (b >= 1024 ** 3) return `${(b / 1024 ** 3).toFixed(2)} GB`
  if (b >= 1024 ** 2) return `${(b / 1024 ** 2).toFixed(0)} MB`
  return `${(b / 1024).toFixed(0)} KB`
}

function formatEta(sec: number): string {
  if (sec >= 3600) return `${Math.floor(sec / 3600)}h ${Math.floor((sec % 3600) / 60)}m`
  if (sec >= 60) return `${Math.floor(sec / 60)}m ${sec % 60}s`
  return `${sec}s`
}

/* ── small components ──────────────────────────────────────────────────── */

function FileSlot({ label, icon, file, accept, onPick, onClear, disabled, required }:
  { label: string; icon: React.ReactNode; file: File | null; accept: string;
    onPick: (f: File) => void; onClear: () => void; disabled: boolean; required?: boolean }) {
  const ref = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const sizeLabel = file
    ? file.size > 1024 * 1024 * 1024
      ? `${(file.size / (1024 ** 3)).toFixed(2)} GB`
      : file.size > 1024 * 1024
        ? `${(file.size / (1024 ** 2)).toFixed(0)} MB`
        : `${(file.size / 1024).toFixed(0)} KB`
    : ''
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false)
    if (disabled) return
    const f = e.dataTransfer.files?.[0]
    if (f) onPick(f)
  }, [disabled, onPick])
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault(); if (!disabled) setDragOver(true)
  }, [disabled])
  const handleDragLeave = useCallback(() => setDragOver(false), [])
  return (
    <div className={`um-slot${file ? ' has-file' : ''}${dragOver ? ' drag-over' : ''}`}
         onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}>
      <input ref={ref} type="file" accept={accept} hidden
             onChange={(e) => { const f = e.target.files?.[0]; if (f) onPick(f); e.target.value = '' }}
             disabled={disabled} />
      <div className="um-slot__head">
        <span className="um-slot__icon">{icon}</span>
        <span className="um-slot__label">{label}{required && <span className="um-required"> *</span>}</span>
      </div>
      {file ? (
        <div className="um-slot__file">
          <span className="um-slot__name">{file.name}</span>
          <span className="um-slot__size">{sizeLabel}</span>
          <button className="um-file__clear" onClick={onClear} title="Remove" aria-label="Remove">
            <X size={12} />
          </button>
        </div>
      ) : (
        <div className="um-slot__drop-zone" onClick={() => !disabled && ref.current?.click()}>
          <span className="um-slot__drop-text">
            Drag a file here or <button className="um-slot__browse" onClick={(e) => { e.stopPropagation(); ref.current?.click() }} disabled={disabled}>browse</button>
          </span>
        </div>
      )}
    </div>
  )
}

/* ── main component ────────────────────────────────────────────────────── */

export default function UploadMatch({ session }: { session: Session }) {
  // Metadata
  const [gameCode, setGameCode] = useState('')
  const [matchDate, setMatchDate] = useState('')
  const [direction, setDirection] = useState<'left' | 'right' | ''>('')
  const [homeColor, setHomeColor] = useState('')
  const [pitchLength, setPitchLength] = useState('')
  const [pitchWidth, setPitchWidth] = useState('')

  // Game timing (collapsible)
  const [timingOpen, setTimingOpen] = useState(false)
  const [kickoffTime, setKickoffTime] = useState('')
  const [halftimeTime, setHalftimeTime] = useState('')
  const [secondHalfTime, setSecondHalfTime] = useState('')
  const [finalWhistleTime, setFinalWhistleTime] = useState('')

  // Extra time
  const [etDirection, setEtDirection] = useState<'left' | 'right' | ''>('')
  const [et1StartTime, setEt1StartTime] = useState('')
  const [et1EndTime, setEt1EndTime] = useState('')
  const [et2StartTime, setEt2StartTime] = useState('')
  const [et2EndTime, setEt2EndTime] = useState('')


  // Files
  const [videoFile, setVideoFile] = useState<File | null>(null)
  const [trackingFile, setTrackingFile] = useState<File | null>(null)
  const [eventFile, setEventFile] = useState<File | null>(null)
  const [supplementalFile, setSupplementalFile] = useState<File | null>(null)
  const [supplementalDesc, setSupplementalDesc] = useState('')

  // Upload state
  const [state, setState] = useState<UploadState>('idle')
  const [progress, setProgress] = useState(0)
  const [uploadingLabel, setUploadingLabel] = useState('')
  const [uploadedBytes, setUploadedBytes] = useState(0)
  const [totalBytes, setTotalBytes] = useState(0)
  const [etaSeconds, setEtaSeconds] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [results, setResults] = useState<string[]>([])
  const xhrRef = useRef<XMLHttpRequest | null>(null)
  const uploadStartRef = useRef(0)
  const lastSampleRef = useRef({ time: 0, loaded: 0 })
  const speedRef = useRef(0) // smoothed bytes/sec

  // Job tracking (declared early so reset() can reference them)
  const [jobId, setJobId] = useState<string | null>(null)
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null)
  // True after ~6 consecutive status-poll failures (~30s dark): the spinner
  // alone would otherwise claim progress it cannot see.
  const [pollStalled, setPollStalled] = useState(false)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [pendingJobs, setPendingJobs] = useState<PendingJob[]>([])

  // Priori staff see real pipeline states and internal identifiers;
  // customers see only upload → processing → completed/failed.
  const isPrioriStaff = !!(session.user?.is_staff || session.user?.is_platform_admin)

  // The old event name kept although the modal became a page — Mixpanel
  // funnels key on the name, and renaming it would orphan them.
  useEffect(() => {
    trackEvent('Upload Modal Opened', { surface: 'page' })
  }, [])

  // Org selector for platform admins / multi-org users
  const orgs = session.organizations ?? []
  const isMultiOrg = orgs.length > 1 || session.user?.is_platform_admin
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null)
  const activeOrg = selectedOrgId
    ? orgs.find(o => o.id === selectedOrgId) ?? session.org
    : session.org
  const club = activeOrg?.name ?? 'Your club'

  // Build query suffix for org override
  const orgQuery = selectedOrgId ? `?organization_id=${selectedOrgId}` : ''
  const orgBody = selectedOrgId ? { organization_id: selectedOrgId } : {}

  const homeTeam = useMemo(() => {
    if (!gameCode.includes('@')) return ''
    return gameCode.split('@')[1]?.trim() || ''
  }, [gameCode])

  const hasFile = !!(videoFile || trackingFile)
  const canSubmit = !!(
    hasFile && gameCode.includes('@') && matchDate && state === 'idle'
  )

  const reset = useCallback(() => {
    if (xhrRef.current) { xhrRef.current.abort(); xhrRef.current = null }
    setGameCode(''); setMatchDate(''); setDirection(''); setHomeColor('')
    setPitchLength(''); setPitchWidth('')
    setTimingOpen(false); setKickoffTime(''); setHalftimeTime('')
    setSecondHalfTime(''); setFinalWhistleTime('')
    setEtDirection(''); setEt1StartTime(''); setEt1EndTime('')
    setEt2StartTime(''); setEt2EndTime('')
    setVideoFile(null); setTrackingFile(null); setEventFile(null)
    setSupplementalFile(null); setSupplementalDesc('')
    setState('idle'); setProgress(0); setUploadingLabel('')
    setUploadedBytes(0); setTotalBytes(0); setEtaSeconds(null)
    speedRef.current = 0
    setError(null); setResults([])
    setJobId(null); setJobStatus(null)
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
  }, [])

  const uploadOneFile = useCallback(async (file: File, label: string, extraBody?: Record<string, unknown>) => {
    setUploadingLabel(label)
    setProgress(0)
    const body = await api<PresignResponse>(`/api/v1/uploads/presign${orgQuery}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filename: file.name,
        content_type: file.type || 'application/octet-stream',
        file_size: file.size,
        game_code: gameCode.trim(),
        match_date: matchDate,
        file_category: label.toLowerCase().replace(/\s/g, '_'),
        home_team_start_direction: direction || undefined,
        home_team_color: homeColor || undefined,
        pitch_length: pitchLength ? Number(pitchLength) : undefined,
        pitch_width: pitchWidth ? Number(pitchWidth) : undefined,
        kickoff_time: kickoffTime || undefined,
        halftime_time: halftimeTime || undefined,
        second_half_time: secondHalfTime || undefined,
        final_whistle_time: finalWhistleTime || undefined,
        et_direction: etDirection || undefined,
        et1_start_time: et1StartTime || undefined,
        et1_end_time: et1EndTime || undefined,
        et2_start_time: et2StartTime || undefined,
        et2_end_time: et2EndTime || undefined,
        supplemental_description: label === 'supplemental' ? supplementalDesc : undefined,
        ...extraBody,
      }),
    })
    setTotalBytes(file.size)
    setUploadedBytes(0)
    setEtaSeconds(null)
    speedRef.current = 0
    uploadStartRef.current = performance.now()
    lastSampleRef.current = { time: performance.now(), loaded: 0 }
    // The bytes are moving from HERE, not after they have all arrived. This
    // line used to sit after the await below, so the label read "Preparing…"
    // and the byte counter and ETA stayed hidden for the whole transfer.
    setState('uploading')

    /** Progress for the file as a whole — one place, so the single-PUT and
     *  multipart paths report the same numbers. */
    const report = (loaded: number) => {
      setProgress(Math.round(100 * loaded / file.size))
      setUploadedBytes(loaded)
      const now = performance.now()
      const prev = lastSampleRef.current
      const dt = (now - prev.time) / 1000
      if (dt > 0.5) { // sample every 500ms minimum to avoid jitter
        const instantSpeed = (loaded - prev.loaded) / dt
        const alpha = Math.min(1, dt / 3) // ~3s smoothing window
        speedRef.current = speedRef.current > 0
          ? speedRef.current * (1 - alpha) + instantSpeed * alpha
          : instantSpeed
        lastSampleRef.current = { time: now, loaded }
        if (speedRef.current > 0) {
          setEtaSeconds(Math.round((file.size - loaded) / speedRef.current))
        }
      }
    }

    /** One PUT, resolving with the ETag when the browser is allowed to read
     *  it (cross-origin, that needs the bucket's CORS to expose it). */
    const put = (url: string, blob: Blob, contentType?: string) =>
      new Promise<string | null>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        xhrRef.current = xhr
        xhr.open('PUT', url)
        if (contentType) xhr.setRequestHeader('Content-Type', contentType)
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) onChunkProgress(e.loaded)
        }
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.getResponseHeader('ETag'))
          else reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`))
        }
        xhr.onerror = () => reject(new Error('Network error during upload'))
        xhr.onabort = () => reject(new Error('Upload canceled'))
        xhr.send(blob)
      })

    // Rebound on each part so progress is absolute across the whole file.
    let onChunkProgress = (loaded: number) => report(loaded)

    if (body.multipart && body.job_id && body.part_size && body.total_parts) {
      const jobId = body.job_id
      await uploadInParts(file, {
        jobId, partSize: body.part_size, totalParts: body.total_parts,
      }, {
        presignPart: async (id, partNumber) => {
          const signed = await api<{ upload_url: string }>(
            '/api/v1/workbench/videos/multipart/presign-part', {
              method: 'POST',
              body: JSON.stringify({ job_id: id, part_number: partNumber }),
            })
          return signed.upload_url
        },
        put: (url, blob, onLoaded) => {
          onChunkProgress = onLoaded
          return put(url, blob)
        },
        // An empty parts list is not a failure: the bucket's CORS may hide
        // the ETag header, and the server asks R2 for the list instead.
        complete: (id, parts) => api('/api/v1/workbench/videos/multipart/complete', {
          method: 'POST',
          body: JSON.stringify({ job_id: id, parts }),
        }).then(() => undefined),
        onProgress: report,
      })
      xhrRef.current = null
      return { r2_key: body.r2_key, job_id: body.job_id ?? null }
    }

    if (!body.upload_url) throw new Error('The server did not return an upload URL')
    await put(body.upload_url, file, file.type || undefined)
    xhrRef.current = null
    return { r2_key: body.r2_key, job_id: body.job_id ?? null }
  }, [gameCode, matchDate, direction, homeColor, pitchLength, pitchWidth,
      kickoffTime, halftimeTime, secondHalfTime, finalWhistleTime,
      etDirection, et1StartTime, et1EndTime, et2StartTime, et2EndTime, supplementalDesc, orgQuery])

  // Pending uploads (loaded on mount and when org changes)
  useEffect(() => {
    let cancelled = false
    api<{ jobs: PendingJob[] }>(`/api/v1/uploads/pending${orgQuery}`).then(res => {
      if (!cancelled) setPendingJobs(res.jobs)
    }).catch(() => {})
    return () => { cancelled = true }
  }, [orgQuery])

  // Refresh pending list after an upload completes or errors
  const refreshPending = useCallback(() => {
    api<{ jobs: PendingJob[] }>(`/api/v1/uploads/pending${orgQuery}`).then(res => {
      setPendingJobs(res.jobs)
    }).catch(() => {})
  }, [orgQuery])

  // Status polling
  const startPolling = useCallback((jid: string) => {
    if (pollRef.current) clearInterval(pollRef.current)
    let failures = 0
    pollRef.current = setInterval(async () => {
      try {
        const s = await api<JobStatus>(`/api/v1/uploads/status/${jid}${orgQuery}`)
        failures = 0
        setPollStalled(false)
        setJobStatus(s)
        if (s.terminal) {
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
          // PROCESSING finishing, which only staff watch here. The upload
          // itself is 'Upload Completed', fired for everyone in upload().
          trackEvent(s.status === 'completed' ? 'Upload Processing Completed'
                                              : 'Upload Processing Failed',
                     { job_id: jid, success: s.status === 'completed' })
          setState(s.status === 'completed' ? 'done' : 'error')
          // Backend error messages carry pipeline internals (pod ids,
          // provider errors) — staff-only. Customers get the plain fact.
          if (s.error) setError(isPrioriStaff ? s.error : 'Processing failed')
          refreshPending()
        }
      } catch {
        // One blip is silent; a stretch of them must not be an eternal
        // spinner. ~6 misses at 5s ≈ 30s dark — say so, keep retrying.
        failures += 1
        if (failures >= 6) setPollStalled(true)
      }
    }, 5000)
  }, [orgQuery, refreshPending, isPrioriStaff])

  // Cancel / abort
  const cancel = useCallback(async () => {
    // If XHR is in flight, abort the browser upload
    if (xhrRef.current) { xhrRef.current.abort(); xhrRef.current = null }
    // If we have a job_id, tell the API to cancel it
    if (jobId) {
      try {
        await api(`/api/v1/uploads/abort${orgQuery}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ job_id: jobId, ...orgBody }),
        })
      } catch { /* best effort */ }
    }
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    reset()
    refreshPending()
  }, [jobId, reset, refreshPending])

  const upload = useCallback(async () => {
    if (!canSubmit) return
    setState('presigning')
    setError(null)
    setResults([])
    setJobId(null)
    setJobStatus(null)

    const uploaded: string[] = []
    const dataFiles: { category: string; r2_key: string; filename: string; size_bytes: number }[] = []
    // What every Upload Completed carries (Mixpanel review, 2026-10-21):
    // customers never poll processing, so this — not the poll — is the
    // event that says an upload finished.
    const picked = [videoFile, trackingFile, eventFile, supplementalFile]
      .filter((f): f is File => f !== null)
    const uploadProps = {
      game_code: gameCode.trim(),
      file_count: picked.length,
      total_mb: Math.round(picked.reduce((n, f) => n + f.size, 0) / (1024 * 1024)),
    }
    let videoJobId: string | null = null
    let videoR2Key: string | null = null
    let allUploaded = false
    try {
      // Upload files sequentially: video, tracking, event, supplemental.
      if (videoFile) {
        const { r2_key, job_id } = await uploadOneFile(videoFile, 'video')
        uploaded.push(`Video: ${r2_key}`)
        if (job_id) { videoJobId = job_id; setJobId(job_id) }
        videoR2Key = r2_key
      }
      if (trackingFile) {
        const { r2_key } = await uploadOneFile(trackingFile, 'tracking_data')
        uploaded.push(`Tracking: ${r2_key}`)
        dataFiles.push({ category: 'tracking_data', r2_key, filename: trackingFile.name, size_bytes: trackingFile.size })
      }
      if (eventFile) {
        const { r2_key } = await uploadOneFile(eventFile, 'event_data')
        uploaded.push(`Events: ${r2_key}`)
        dataFiles.push({ category: 'event_data', r2_key, filename: eventFile.name, size_bytes: eventFile.size })
      }
      if (supplementalFile) {
        const { r2_key } = await uploadOneFile(supplementalFile, 'supplemental')
        uploaded.push(`Supplemental: ${r2_key}`)
        dataFiles.push({ category: 'supplemental', r2_key, filename: supplementalFile.name, size_bytes: supplementalFile.size })
      }

      setResults(uploaded)
      setProgress(100)
      allUploaded = true

      // Call /complete to verify R2 and dispatch video processing
      if (videoJobId && videoR2Key) {
        setState('completing')
        const completeRes = await api<{ success: boolean; message?: string; status?: string }>(
          `/api/v1/uploads/complete${orgQuery}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ job_id: videoJobId, r2_key: videoR2Key, ...orgBody }),
          })
        if (completeRes.success && isPrioriStaff) {
          setState('processing')
          startPolling(videoJobId)
        } else if (completeRes.success) {
          // A customer is told it worked and it will be processed — no
          // live pipeline view to sit and watch (Ben, 2026-10-03).
          setState('done')
        } else {
          setState('done')
        }
        // Also notify about data files if any were uploaded alongside video
        if (dataFiles.length > 0) {
          api(`/api/v1/uploads/notify${orgQuery}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ game_code: gameCode, match_date: matchDate, files: dataFiles }),
          }).catch(() => {}) // best effort
        }
      } else if (dataFiles.length > 0) {
        // Data-only upload (no video) — notify to register files and trigger import
        setState('completing')
        const notifyRes = await api<{ success: boolean; message?: string; tracking_job_id?: string }>(
          `/api/v1/uploads/notify${orgQuery}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ game_code: gameCode, match_date: matchDate, files: dataFiles }),
          })
        if (notifyRes.tracking_job_id) {
          uploaded.push(`Tracking import queued: ${notifyRes.tracking_job_id}`)
          setResults([...uploaded])
        }
        setState('done')
      } else {
        setState('done')
      }
      trackEvent('Upload Completed', { ...uploadProps, success: true, job_id: videoJobId })
      refreshPending()
    } catch (e: unknown) {
      xhrRef.current = null
      if (e instanceof Error && e.message === 'Upload canceled') return
      setResults(uploaded)
      setState('error')
      const detail = e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e)
      trackEvent('Upload Completed', {
        ...uploadProps, success: false, job_id: videoJobId,
        // which half failed: sending the files, or starting processing
        stage: allUploaded ? 'processing_start' : 'upload',
        error: detail.slice(0, 200),
      })
      // Staff get the real reason; a customer whose files all arrived was
      // let down by processing, not by their upload.
      setError(isPrioriStaff ? detail
        : allUploaded ? PROCESSING_NOT_STARTED : UPLOAD_FAILED)
      refreshPending()
    }
  }, [canSubmit, videoFile, trackingFile, eventFile, supplementalFile,
      uploadOneFile, startPolling, refreshPending, isPrioriStaff, gameCode])

  // Cleanup polling on unmount
  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current)
  }, [])

  const formDisabled = state !== 'idle'

  // ── Existing-match data upload ─────────────────────────────────────
  const [existingGames, setExistingGames] = useState<ExistingGame[]>([])
  const [existingOpen, setExistingOpen] = useState(false)
  const [selectedGameId, setSelectedGameId] = useState<number | null>(null)
  const [existingTrackingFile, setExistingTrackingFile] = useState<File | null>(null)
  const [existingEventFile, setExistingEventFile] = useState<File | null>(null)
  const [existingSupplementalFile, setExistingSupplementalFile] = useState<File | null>(null)
  const [existingState, setExistingState] = useState<'idle' | 'uploading' | 'done' | 'error'>('idle')
  const [existingProgress, setExistingProgress] = useState(0)
  const [existingLabel, setExistingLabel] = useState('')
  const [existingResults, setExistingResults] = useState<string[]>([])
  const [existingError, setExistingError] = useState<string | null>(null)
  // Reuse upload speed state for existing-match uploads
  const [existingUploadedBytes, setExistingUploadedBytes] = useState(0)
  const [existingTotalBytes, setExistingTotalBytes] = useState(0)
  const [existingEta, setExistingEta] = useState<number | null>(null)
  const existingSpeedRef = useRef(0)
  const existingLastSampleRef = useRef({ time: 0, loaded: 0 })
  const existingXhrRef = useRef<XMLHttpRequest | null>(null)

  // Load existing games when the section opens or org changes
  useEffect(() => {
    if (!existingOpen) return
    api<{ games: ExistingGame[] }>(`/api/v1/uploads/games${orgQuery}`).then(res => {
      setExistingGames(res.games)
    }).catch(() => {})
  }, [existingOpen, orgQuery])

  const selectedGame = existingGames.find(g => g.id === selectedGameId)
  const existingHasFile = !!(existingTrackingFile || existingEventFile || existingSupplementalFile)

  const uploadExistingFile = useCallback(async (file: File, label: string, gameCode: string, matchDate: string | null) => {
    setExistingLabel(label)
    setExistingProgress(0)
    setExistingTotalBytes(file.size)
    setExistingUploadedBytes(0)
    setExistingEta(null)
    existingSpeedRef.current = 0
    existingLastSampleRef.current = { time: performance.now(), loaded: 0 }
    const category = label === 'tracking' ? 'tracking_data' : label === 'events' ? 'event_data' : 'supplemental'
    const body = await api<PresignResponse>(`/api/v1/uploads/presign${orgQuery}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filename: file.name,
        game_code: gameCode,
        match_date: matchDate,
        // file_category, NOT category: the server reads `file_category` and
        // defaults it to 'video'. Sending the wrong key made every data file
        // added to an existing match presign as a VIDEO — which ran the
        // duplicate-job guard (a 409 "already has a job in progress" when the
        // game was mid-pipeline, which is exactly when someone adds tracking)
        // and wrote the file to the video input key, where the tracking
        // importer does not look.
        file_category: category,
      }),
    })
    // Captured, not read inside the closure: narrowing does not survive the
    // callback boundary.
    const putUrl = body.upload_url
    if (!putUrl) throw new Error('The server did not return an upload URL')
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      existingXhrRef.current = xhr
      xhr.open('PUT', putUrl)
      if (file.type) xhr.setRequestHeader('Content-Type', file.type)
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return
        setExistingProgress(Math.round(100 * e.loaded / e.total))
        setExistingUploadedBytes(e.loaded)
        const now = performance.now()
        const prev = existingLastSampleRef.current
        const dt = (now - prev.time) / 1000
        if (dt > 0.5) {
          const speed = (e.loaded - prev.loaded) / dt
          const alpha = Math.min(1, dt / 3)
          existingSpeedRef.current = existingSpeedRef.current > 0
            ? existingSpeedRef.current * (1 - alpha) + speed * alpha : speed
          existingLastSampleRef.current = { time: now, loaded: e.loaded }
          if (existingSpeedRef.current > 0) setExistingEta(Math.round((e.total - e.loaded) / existingSpeedRef.current))
        }
      }
      xhr.onload = () => { if (xhr.status >= 200 && xhr.status < 300) resolve(); else reject(new Error(`Upload failed: ${xhr.status}`)) }
      xhr.onerror = () => reject(new Error('Network error'))
      xhr.onabort = () => reject(new Error('Upload canceled'))
      xhr.send(file)
    })
    existingXhrRef.current = null
    return { r2_key: body.r2_key, category, filename: file.name, size_bytes: file.size }
  }, [orgQuery])

  const uploadExisting = useCallback(async () => {
    if (!selectedGame || !existingHasFile) return
    setExistingState('uploading')
    setExistingError(null)
    setExistingResults([])
    const uploaded: string[] = []
    const dataFiles: { category: string; r2_key: string; filename: string; size_bytes: number }[] = []
    try {
      if (existingTrackingFile) {
        const f = await uploadExistingFile(existingTrackingFile, 'tracking', selectedGame.game_code, selectedGame.match_date)
        uploaded.push(`Tracking: ${f.r2_key}`)
        dataFiles.push(f)
      }
      if (existingEventFile) {
        const f = await uploadExistingFile(existingEventFile, 'events', selectedGame.game_code, selectedGame.match_date)
        uploaded.push(`Events: ${f.r2_key}`)
        dataFiles.push(f)
      }
      if (existingSupplementalFile) {
        const f = await uploadExistingFile(existingSupplementalFile, 'supplemental', selectedGame.game_code, selectedGame.match_date)
        uploaded.push(`Supplemental: ${f.r2_key}`)
        dataFiles.push(f)
      }
      // Notify
      if (dataFiles.length > 0) {
        const res = await api<{ success: boolean; message?: string; tracking_job_id?: string }>(
          `/api/v1/uploads/notify${orgQuery}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ game_code: selectedGame.game_code, match_date: selectedGame.match_date, files: dataFiles }),
          })
        if (res.tracking_job_id) uploaded.push(`Tracking import queued`)
      }
      setExistingResults(uploaded)
      setExistingState('done')
      refreshPending()
    } catch (e: unknown) {
      existingXhrRef.current = null
      if (e instanceof Error && e.message === 'Upload canceled') return
      setExistingResults(uploaded)
      setExistingState('error')
      setExistingError(isPrioriStaff ? (e instanceof Error ? e.message : String(e)) : UPLOAD_FAILED)
    }
  }, [selectedGame, existingHasFile, existingTrackingFile, existingEventFile, existingSupplementalFile, uploadExistingFile, refreshPending, isPrioriStaff])

  const resetExisting = useCallback(() => {
    if (existingXhrRef.current) { existingXhrRef.current.abort(); existingXhrRef.current = null }
    setSelectedGameId(null)
    setExistingTrackingFile(null); setExistingEventFile(null)
    setExistingSupplementalFile(null)
    setExistingState('idle'); setExistingProgress(0); setExistingLabel('')
    setExistingUploadedBytes(0); setExistingTotalBytes(0); setExistingEta(null)
    setExistingResults([]); setExistingError(null)
  }, [])

  return (
    <>
      <h1 className="page-title">Upload match</h1>
      <p className="page-sub">
        Upload video and data for <strong>{club}</strong>.
        {!isMultiOrg && ' At least one of video or tracking data is required.'}
      </p>

      {isMultiOrg && (
        <div className="um-field" style={{ maxWidth: 320, marginBottom: 'var(--space-4)' }}>
          <label className="um-label" htmlFor="um-org-picker">Organization</label>
          <select id="um-org-picker" className="um-input"
                  value={selectedOrgId ?? session.org?.id ?? ''}
                  onChange={(e) => {
                    const id = Number(e.target.value)
                    setSelectedOrgId(id === session.org?.id ? null : id)
                  }}
                  disabled={formDisabled}>
            {orgs.map(o => (
              <option key={o.id} value={o.id}>{o.name} ({o.code})</option>
            ))}
          </select>
        </div>
      )}

      {/* ── add data to existing match ─────────────────────────────── */}
      <div className="um-timing-header" style={{ marginTop: 'var(--space-4)' }}>
        <button className="um-collapse-btn" onClick={() => setExistingOpen(!existingOpen)} type="button">
          {existingOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          Add data to existing match
        </button>
      </div>
      {existingOpen && (
        <div className="um-existing">
          <div className="um-field" style={{ marginBottom: 'var(--space-4)' }}>
            <label className="um-label" htmlFor="um-existing-game">Match</label>
            <select id="um-existing-game" className="um-input"
                    value={selectedGameId ?? ''}
                    onChange={(e) => setSelectedGameId(e.target.value ? Number(e.target.value) : null)}
                    disabled={existingState !== 'idle'}>
              <option value="">Select a match…</option>
              {existingGames.map(g => (
                <option key={g.id} value={g.id}>
                  {g.game_code}{g.match_date ? ` (${g.match_date})` : ''}
                  {g.has_video ? ' — video' : ''}{g.has_tracking ? ' — tracking' : ''}
                </option>
              ))}
            </select>
          </div>

          {selectedGameId && existingState === 'idle' && (
            <div className="um-slots">
              <FileSlot label="Tracking data" icon={<FileSpreadsheet size={16} />}
                file={existingTrackingFile} accept=".parquet,.json,.csv,.xml"
                onPick={setExistingTrackingFile} onClear={() => setExistingTrackingFile(null)}
                disabled={existingState !== 'idle'} />
              <FileSlot label="Event data" icon={<FileText size={16} />}
                file={existingEventFile} accept=".xml,.json,.csv"
                onPick={setExistingEventFile} onClear={() => setExistingEventFile(null)}
                disabled={existingState !== 'idle'} />
              <FileSlot label="Supplemental file" icon={<Upload size={16} />}
                file={existingSupplementalFile} accept="*"
                onPick={setExistingSupplementalFile} onClear={() => setExistingSupplementalFile(null)}
                disabled={existingState !== 'idle'} />
            </div>
          )}

          {existingState === 'uploading' && (
            <div className="um-progress">
              <div className="um-progress__bar">
                <div className="um-progress__fill" style={{ width: `${existingProgress}%` }} />
              </div>
              <div className="um-progress__details">
                <span className="um-progress__label">Uploading {existingLabel}… {existingProgress}%</span>
                {existingTotalBytes > 0 && (
                  <span className="um-progress__eta">
                    {formatBytes(existingUploadedBytes)} / {formatBytes(existingTotalBytes)}
                    {existingEta != null && existingEta > 0 && ` — ${formatEta(existingEta)} remaining`}
                  </span>
                )}
              </div>
            </div>
          )}

          {existingState === 'done' && existingResults.length > 0 && (
            <div className="um-done">
              <CheckCircle size={20} />
              <div>
                <p className="um-done__title">Data uploaded for {selectedGame?.game_code}</p>
                {!isPrioriStaff && <p className="um-done__note">{UPLOAD_RECEIVED}</p>}
                {isPrioriStaff && existingResults.map((r, i) => <p key={i} className="um-done__key">{r}</p>)}
              </div>
            </div>
          )}

          {existingState === 'error' && (
            <p className="error">{existingError}</p>
          )}

          <div className="um-actions">
            {existingState === 'idle' && selectedGameId && existingHasFile && (
              <button className="adm-btn" onClick={uploadExisting}>
                Upload {[existingTrackingFile, existingEventFile, existingSupplementalFile]
                  .filter(Boolean).length} file{[existingTrackingFile, existingEventFile, existingSupplementalFile]
                  .filter(Boolean).length !== 1 ? 's' : ''}
              </button>
            )}
            {existingState === 'uploading' && (
              <button className="adm-btn adm-btn--quiet" onClick={() => {
                if (existingXhrRef.current) { existingXhrRef.current.abort(); existingXhrRef.current = null }
                resetExisting()
              }}>Cancel</button>
            )}
            {(existingState === 'done' || existingState === 'error') && (
              <button className="adm-btn" onClick={resetExisting}>Upload more data</button>
            )}
          </div>
        </div>
      )}

      {/* ── new match: game metadata ───────────────────────────────── */}
      <h2 className="um-section-title">New match</h2>

      {/* ── game metadata ──────────────────────────────────────────── */}
      <div className="um-form">
        <div className="um-field">
          <label className="um-label" htmlFor="um-game-code">
            Game code <span className="um-required">*</span>
          </label>
          <input id="um-game-code" className="um-input" type="text"
            placeholder="AWAY@HOME (e.g. SKC@LAG)"
            value={gameCode}
            onChange={(e) => setGameCode(e.target.value.toUpperCase())}
            disabled={formDisabled} />
          {gameCode && !gameCode.includes('@') && (
            <span className="um-hint um-hint--warn">Must contain @</span>
          )}
          {homeTeam && (
            <span className="um-hint">{homeTeam} set to be home team</span>
          )}
        </div>

        <div className="um-field">
          <label className="um-label" htmlFor="um-match-date">
            Match date <span className="um-required">*</span>
          </label>
          <input id="um-match-date" className="um-input" type="date"
            value={matchDate} onChange={(e) => setMatchDate(e.target.value)}
            disabled={formDisabled} />
        </div>

        <div className="um-field">
          <label className="um-label" htmlFor="um-direction">
            {homeTeam || 'Home team'} start attacking direction
          </label>
          <select id="um-direction" className="um-input" value={direction}
            onChange={(e) => setDirection(e.target.value as '' | 'left' | 'right')}
            disabled={formDisabled}>
            <option value="">Unknown</option>
            <option value="left">Left (home attacking left)</option>
            <option value="right">Right (home attacking right)</option>
          </select>
        </div>

        <div className="um-field">
          <label className="um-label" htmlFor="um-home-color">
            {homeTeam || 'Home team'} jersey color
          </label>
          <div className="um-color-row">
            <select id="um-home-color" className="um-input" value={homeColor}
              onChange={(e) => setHomeColor(e.target.value)} disabled={formDisabled}>
              {JERSEY_COLORS.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
            {homeColor && (
              <span className="um-color-swatch-preview" style={{ background: homeColor }} />
            )}
          </div>
        </div>

        <div className="um-field um-field--row">
          <div>
            <label className="um-label" htmlFor="um-pitch-length">Pitch length (m)</label>
            <input id="um-pitch-length" className="um-input um-input--narrow" type="number"
              min="90" max="120" placeholder="105" value={pitchLength}
              onChange={(e) => setPitchLength(e.target.value)} disabled={formDisabled} />
          </div>
          <div>
            <label className="um-label" htmlFor="um-pitch-width">Pitch width (m)</label>
            <input id="um-pitch-width" className="um-input um-input--narrow" type="number"
              min="45" max="90" placeholder="68" value={pitchWidth}
              onChange={(e) => setPitchWidth(e.target.value)} disabled={formDisabled} />
          </div>
        </div>
      </div>

      {/* ── game timing (collapsible) ──────────────────────────────── */}
      <div className="um-timing-header">
        <button className="um-collapse-btn" onClick={() => setTimingOpen(!timingOpen)}
                type="button">
          {timingOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          Game timing
        </button>
        <span className="um-timing-help">
          Times in the video, not match clock. Used for goalkeeper team
          assignment and video sync; leave blank to auto-detect.
        </span>
      </div>
      {timingOpen && (
        <div className="um-form um-form--timing">
          <div className="um-field">
            <label className="um-label" htmlFor="um-kickoff">Kickoff (mm:ss)</label>
            <input id="um-kickoff" className="um-input" type="text"
              placeholder="00:00" value={kickoffTime}
              onChange={(e) => setKickoffTime(e.target.value)} disabled={formDisabled} />
          </div>
          <div className="um-field">
            <label className="um-label" htmlFor="um-halftime">Half-time whistle (mm:ss)</label>
            <input id="um-halftime" className="um-input" type="text"
              placeholder="47:30" value={halftimeTime}
              onChange={(e) => setHalftimeTime(e.target.value)} disabled={formDisabled} />
          </div>
          <div className="um-field">
            <label className="um-label" htmlFor="um-2nd-half">2nd half kickoff (mm:ss)</label>
            <input id="um-2nd-half" className="um-input" type="text"
              placeholder="63:00" value={secondHalfTime}
              onChange={(e) => setSecondHalfTime(e.target.value)} disabled={formDisabled} />
          </div>
          <div className="um-field">
            <label className="um-label" htmlFor="um-final">Final whistle (mm:ss)</label>
            <input id="um-final" className="um-input" type="text"
              placeholder="110:00" value={finalWhistleTime}
              onChange={(e) => setFinalWhistleTime(e.target.value)} disabled={formDisabled} />
          </div>

          {/* ---- extra time (optional) ---- */}
          <div className="um-field" style={{ gridColumn: "1 / -1" }}>
            <span className="um-label" style={{ fontWeight: 600, marginBottom: 4 }}>Extra time</span>
            <span className="um-timing-help">
              New coin toss before extra time: ET direction cannot be inferred from H1.
            </span>
          </div>
          <div className="um-field">
            <label className="um-label" htmlFor="um-et-dir">ET home attacking direction</label>
            <select id="um-et-dir" className="um-input" value={etDirection}
              onChange={(e) => setEtDirection(e.target.value as '' | 'left' | 'right')}
              disabled={formDisabled}>
              <option value="">Unknown</option>
              <option value="left">Left</option>
              <option value="right">Right</option>
            </select>
          </div>
          <div className="um-field">
            <label className="um-label" htmlFor="um-et1-start">ET1 start (mm:ss)</label>
            <input id="um-et1-start" className="um-input" type="text"
              placeholder="93:00" value={et1StartTime}
              onChange={(e) => setEt1StartTime(e.target.value)} disabled={formDisabled} />
          </div>
          <div className="um-field">
            <label className="um-label" htmlFor="um-et1-end">ET1 end (mm:ss)</label>
            <input id="um-et1-end" className="um-input" type="text"
              placeholder="108:00" value={et1EndTime}
              onChange={(e) => setEt1EndTime(e.target.value)} disabled={formDisabled} />
          </div>
          <div className="um-field">
            <label className="um-label" htmlFor="um-et2-start">ET2 start (mm:ss)</label>
            <input id="um-et2-start" className="um-input" type="text"
              placeholder="110:00" value={et2StartTime}
              onChange={(e) => setEt2StartTime(e.target.value)} disabled={formDisabled} />
          </div>
          <div className="um-field">
            <label className="um-label" htmlFor="um-et2-end">ET2 end (mm:ss)</label>
            <input id="um-et2-end" className="um-input" type="text"
              placeholder="125:00" value={et2EndTime}
              onChange={(e) => setEt2EndTime(e.target.value)} disabled={formDisabled} />
          </div>
        </div>
      )}

      {/* ── file slots ─────────────────────────────────────────────── */}
      <h2 className="um-section-title">Files</h2>
      {!hasFile && state === 'idle' && (
        <p className="um-hint um-hint--warn" style={{ marginBottom: 8 }}>
          At least one of Video or Tracking Data is required
        </p>
      )}
      <div className="um-slots">
        <FileSlot label="Match video" icon={<Film size={16} />}
          file={videoFile} accept="video/*"
          onPick={setVideoFile} onClear={() => setVideoFile(null)}
          disabled={formDisabled} />

        <FileSlot label="Tracking data" icon={<FileSpreadsheet size={16} />}
          file={trackingFile} accept=".parquet,.json,.csv,.xml"
          onPick={setTrackingFile} onClear={() => setTrackingFile(null)}
          disabled={formDisabled} />

        <div>
          <FileSlot label="Event data" icon={<FileText size={16} />}
            file={eventFile} accept=".xml,.json,.csv"
            onPick={setEventFile} onClear={() => setEventFile(null)}
            disabled={formDisabled} />
          <p className="um-slot-help">
            Ingested after tracking is ready. Supported: Opta F24. Other
            formats are stored but not parsed yet.
          </p>
        </div>

        {/* Plain wrapper like the event-data slot above; the description
            input carries its own styled class (um-supplemental-desc). */}
        <div>
          <FileSlot label="Supplemental file" icon={<Upload size={16} />}
            file={supplementalFile} accept="*"
            onPick={setSupplementalFile} onClear={() => { setSupplementalFile(null); setSupplementalDesc('') }}
            disabled={formDisabled} />
          {supplementalFile && (
            <input className="um-input um-supplemental-desc" type="text"
              placeholder="Describe this file (e.g. lineup sheet, tactical notes)"
              value={supplementalDesc}
              onChange={(e) => setSupplementalDesc(e.target.value)}
              disabled={formDisabled} />
          )}
        </div>
      </div>

      {/* ── progress / status ──────────────────────────────────────── */}
      {(state === 'presigning' || state === 'uploading') && (
        <div className="um-progress">
          <div className="um-progress__bar">
            <div className="um-progress__fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="um-progress__details">
            <span className="um-progress__label">
              {state === 'presigning' ? 'Preparing…' : `Uploading ${uploadingLabel}… ${progress}%`}
            </span>
            {state === 'uploading' && totalBytes > 0 && (
              <span className="um-progress__eta">
                {formatBytes(uploadedBytes)} / {formatBytes(totalBytes)}
                {etaSeconds != null && etaSeconds > 0 && ` — ${formatEta(etaSeconds)} remaining`}
              </span>
            )}
          </div>
        </div>
      )}

      {state === 'completing' && (
        <div className="um-progress">
          <div className="um-progress__bar">
            <div className="um-progress__fill" style={{ width: '100%' }} />
          </div>
          <span className="um-progress__label">
            {isPrioriStaff ? 'Verifying upload and dispatching processing…' : 'Finishing upload…'}
          </span>
        </div>
      )}

      {state === 'processing' && jobStatus && (
        <div className="um-done" style={{ color: 'var(--color-accent)' }}>
          <Loader2 size={20} className="spin" />
          <div>
            <p className="um-done__title">
              {pollStalled ? 'Status unavailable — retrying…'
                : isPrioriStaff ? jobStatus.message : 'Processing…'}
            </p>
            {isPrioriStaff && jobStatus.calibration_url && (
              <p className="um-done__key">
                <a href={jobStatus.calibration_url}>Review calibration</a>
              </p>
            )}
            {isPrioriStaff && <p className="um-done__key">Job: {jobStatus.job_id}</p>}
          </div>
        </div>
      )}

      {state === 'done' && results.length > 0 && (
        <div className="um-done">
          <CheckCircle size={20} />
          <div>
            <p className="um-done__title">
              {!isPrioriStaff ? 'Upload successful'
                : jobStatus?.status === 'completed' ? 'Processing complete'
                  : `Upload complete — ${results.length} file${results.length > 1 ? 's' : ''}`}
            </p>
            {!isPrioriStaff && <p className="um-done__note">Your match will now be processed.</p>}
            {/* R2 keys and job ids are pipeline internals — staff-only. */}
            {isPrioriStaff && results.map((r, i) => <p key={i} className="um-done__key">{r}</p>)}
          </div>
        </div>
      )}

      {state === 'error' && (
        <div>
          {results.length > 0 && (
            <div className="um-done" style={{ marginBottom: 8 }}>
              <CheckCircle size={16} />
              <div>
                <p className="um-done__title">{results.length} file{results.length > 1 ? 's' : ''} uploaded before error</p>
                {isPrioriStaff && results.map((r, i) => <p key={i} className="um-done__key">{r}</p>)}
              </div>
            </div>
          )}
          <p className="error">{error}</p>
        </div>
      )}

      {/* ── actions ────────────────────────────────────────────────── */}
      <div className="um-actions">
        {state === 'idle' && (
          <button className="adm-btn" onClick={upload}
                  disabled={!canSubmit}>
            Upload {[videoFile, trackingFile, eventFile, supplementalFile]
              .filter(Boolean).length} file{[videoFile, trackingFile, eventFile, supplementalFile]
              .filter(Boolean).length !== 1 ? 's' : ''}
          </button>
        )}
        {(state === 'presigning' || state === 'uploading' || state === 'completing') && (
          <button className="adm-btn adm-btn--quiet" onClick={cancel}>Cancel</button>
        )}
        {state === 'processing' && (
          <button className="adm-btn adm-btn--quiet" onClick={cancel}>Cancel processing</button>
        )}
        {(state === 'done' || state === 'error') && (
          <button className="adm-btn" onClick={reset}>Upload another match</button>
        )}
      </div>

      {/* ── pending uploads ────────────────────────────────────────── */}
      {pendingJobs.length > 0 && (
        <>
          <h2 className="um-section-title" style={{ marginTop: 'var(--space-8)' }}>Recent uploads</h2>
          <div className="um-pending">
            {pendingJobs.map((j) => {
              const shown = isPrioriStaff ? j.status : customerStatus(j.status)
              return (
                <div key={j.id} className={`um-pending__row um-pending--${shown}`}>
                  <span className="um-pending__icon">{STATUS_ICONS[shown] ?? <Loader2 size={14} className="spin" />}</span>
                  <span className="um-pending__code">{j.game_code}</span>
                  <span className="um-pending__file">{j.video_filename ?? '—'}</span>
                  <span className="um-pending__status">{statusLabel(j.status, isPrioriStaff)}</span>
                  {j.video_size_mb && <span className="um-pending__size">{j.video_size_mb} MB</span>}
                  {isPrioriStaff && j.error && <span className="um-pending__error" title={j.error}>Error</span>}
                </div>
              )
            })}
          </div>
        </>
      )}
    </>
  )
}
